import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs';
import { HydraCollection } from '../hydra/hydra-collection';
import { OrganisationProfileMemberModel } from '../models/organisation/organisation-profile';
import { MemberTrainingRightsResult } from '@core/models/organisation/member-training-rights.model';
import { map } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class OrganisationProfileMemberApiService extends ApiService {
  readonly resourcePath = 'organisation_profile_members';

  list(filters: Record<string, string> = {}): Observable<HydraCollection<OrganisationProfileMemberModel>> {
    return this.restList<OrganisationProfileMemberModel>(filters);
  }

  get(id: string): Observable<OrganisationProfileMemberModel> {
    return this.restRead<OrganisationProfileMemberModel>(id);
  }
  getSelf(id: string) {
    return this.http.get(this.resourceUrl+'/'+id+'/self');
  }

  getByOrganisation(id:string):Observable<HydraCollection<OrganisationProfileMemberModel>> {
    return this.http.get<HydraCollection<OrganisationProfileMemberModel>>(this.resourceUrl + "?organisationProfile="+id).pipe(
      map(response => new HydraCollection<OrganisationProfileMemberModel>(response))
    );
  }

  checkConnection(id:string): Observable<HydraCollection<OrganisationProfileMemberModel>> {
    return this.http.get<HydraCollection<OrganisationProfileMemberModel>>(this.resourceUrl + '?organisationProfile=' + id).pipe(
      map(response => new HydraCollection<OrganisationProfileMemberModel>(response))
    );
  }

  // `qualificationId` keeps only the courses and employments related to that qualification.
  getLearningPlanHistory(id:string, qualificationId:string = ''){
    const filter = qualificationId ? '?qualification=' + encodeURIComponent(qualificationId) : '';
    return this.http.get<any>(this.resourceUrl + '/' + id + '/training_plan_history' + filter);
  }

  // The audit log of the member's qualifications: requirements, deadlines, confirmations and
  // certificates, newest first, a page at a time.
  getQualificationHistory(id:string, params:Record<string, string> = {}){
    const query = Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : '';
    return this.http.get<HydraCollection<any>>(this.resourceUrl + '/' + id + '/qualification_history' + query).pipe(
      map(response => new HydraCollection<any>(response))
    );
  }

  generateAiAnalysis(memberId: string) {
    // Assumes the backend endpoint is /{id}/generate_ai_analysis
    return this.http.get<any>(this.resourceUrl + '/' + memberId + '/ai_analysis');
  }

  getTrainingRights(id:string,year:number){
    return this.http.get<any>(this.resourceUrl + '/' + id + '/get_training_rights_for_year/'+year);
  }

  // The rights of a whole page of members in one call, always for the current year. The API accepts
  // at most 200 members and only answers for the members the current user is admin or manager of.
  getTrainingRightsForMembers(memberIds:string[]): Observable<MemberTrainingRightsResult[]> {
    const members = memberIds.map(id => '/' + this.resourcePath + '/' + id);
    return this.http.post<{results:MemberTrainingRightsResult[]}>(this.resourceUrl + '/training_rights', { members: members }).pipe(
      map(response => response.results ?? [])
    );
  }

  downloadTrainingCv(id:string, year:number|null,ext:string, zip:boolean): Observable<any> {
    const yearstring = year? "year="+year : "";

    if(ext == 'mail'){
      return this.http.post(`${this.apiUrl}/me/${this.resourcePath}/${id}/cv/export?${yearstring}&type=${ext}`, {});
    }else{
      if(zip){
        ext = "pdf"
      }
      return this.http.post(`${this.apiUrl}/me/${this.resourcePath}/${id}/cv/export?${yearstring}&type=${ext}&zip=${zip}`, {}, { responseType: 'blob' });
    }
  }


  downloadTrainingCvInBulk(id:string, data:object, year:number|null,ext:string, zip:boolean): Observable<any> {
    const yearstring = year? "year="+year : "";

    if(ext == 'mail'){
      return this.http.post(`${this.apiUrl}/organisation_profiles/${id}/bulk_cv_export?${yearstring}&type=${ext}`, data);
    }else{
      if(zip){
        ext = "pdf"
      }
      return this.http.post(`${this.apiUrl}/organisation_profiles/${id}/bulk_cv_export?${yearstring}&type=${ext}&zip=${zip}`, data, { responseType: 'blob' });
    }
  }

  exportMembers(id:string, type?: 'smartlions' | 'mtechplus'): Observable<any> {
    const query = type ? `?type=${type}` : '';
    return this.http.post(`${this.apiUrl}/organisation_profiles/${id}/members/export${query}`, {}, { responseType: 'blob' });
  }

  // Hands the caller's administrator role over to the given member
  transferAdmin(memberId:string): Observable<OrganisationProfileMemberModel> {
    return this.http.post<OrganisationProfileMemberModel>(this.resourceUrl + '/' + memberId + '/transfer_admin', {});
  }

  addLearningCourses(memberId:string, body:object){
    return this.http.post<any>(this.resourceUrl + '/' + memberId + '/add_bulk_training_plan_courses'  ,body);
  }

  update(id: string, data: object): Observable<OrganisationProfileMemberModel> {
    return this.restUpdate<OrganisationProfileMemberModel>(id, data);
  }

  create(data:object){
    return this.restCreate<OrganisationProfileMemberModel>(data);
  }

  delete(id:string){
    return this.restDelete(id);
  }

  sendVerificationMail(memberId:string,email:string){
    const body = { emailAddress:email };
    return this.http.post<any>(this.resourceUrl + '/' + memberId + '/verify'  ,body);
  }

  verifyMember(code:string, orgId:string = ""){
    const body:any = { verificationCode:code };
    if(orgId){
      body.organisationProfile = orgId;
    }
    return this.http.post<any>(this.resourceUrl + '/verify/accept'  ,body);
  }

  bulkInviteMembers(id:string, data:object){
    return this.http.post<any>(this.apiUrl +'/bulk_member_invite'  ,data);
  }

  renderInviteMail(id:string){
    return this.http.get<any>(this.apiUrl +'/render_profile_member_invite/'+id);
  }

  getExpiredCertificates(id:string){
  return this.http.get<any>(this.resourceUrl + '/' + id +  '/has_overdue_certificates');
  }

  getInvestment(id:string, year:number){
    return this.http.get<any>(this.resourceUrl + '/' + id +  '/budgets/'+year);
  }
}
